import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Shield, Lock } from "lucide-react";

/**
 * Role permission matrix editor (v1.4.3).
 *
 * Loads the org-wide role × permission matrix from
 * GET /api/admin/role-permissions and lets a super_admin toggle each
 * cell. Saved per-row via PUT /api/admin/role-permissions. A "Reset to
 * defaults" button drops the override doc for that role (POST
 * /api/admin/role-permissions/reset).
 *
 * Notes:
 * - super_admin row is rendered read-only — its permissions are
 *   implicitly "all granted" by the backend (auth.require_roles).
 * - Permission groups come from the API so adding a new permission
 *   key is a backend-only change.
 */
export default function RolePermissionsPanel() {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState({}); // { role: { perm: bool } }
  const [savingRole, setSavingRole] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/role-permissions");
      setData(data);
      setDirty({});
    } catch {
      toast.error("Failed to load role permissions");
    }
  };

  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-muted-foreground p-6">Loading permission matrix…</div>;

  const { roles, matrix, permission_groups, defaults } = data;

  const toggle = (role, perm) => {
    if (role === "super_admin") return; // locked
    const current = dirty[role]?.[perm] ?? matrix[role][perm];
    setDirty((d) => ({ ...d, [role]: { ...(d[role] || {}), [perm]: !current } }));
  };

  const effective = (role, perm) => dirty[role]?.[perm] ?? matrix[role][perm];

  const isRowDirty = (role) => dirty[role] && Object.keys(dirty[role]).length > 0;

  const saveRole = async (role) => {
    if (!isRowDirty(role)) return;
    setSavingRole(role);
    try {
      // Send the FULL effective row so the backend has a complete picture
      const merged = { ...matrix[role], ...dirty[role] };
      await api.put("/admin/role-permissions", { role, permissions: merged });
      toast.success(`${role} permissions saved`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingRole(null);
    }
  };

  const resetRole = async (role) => {
    if (role === "super_admin") return;
    if (!window.confirm(`Reset ${role} back to defaults? This drops any custom overrides.`)) return;
    try {
      await api.post("/admin/role-permissions/reset", { role });
      toast.success(`${role} reset to defaults`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="role-permissions-panel">
      <div className="bg-card border border-border rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-4 h-4 mt-0.5 text-primary" strokeWidth={1.75} />
          <div>
            <div className="text-sm font-semibold mb-1">Role permissions matrix</div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              Override per-role permissions for your organisation. <strong>Super admin</strong> always
              has every permission and is intentionally locked. Only super_admins can edit this matrix
              (to prevent admins revoking their own oversight).
            </div>
          </div>
        </div>
      </div>

      {Object.entries(permission_groups).map(([group, perms]) => (
        <div key={group} className="bg-card border border-border rounded-sm overflow-x-auto" data-testid={`perm-group-${group}`}>
          <div className="px-4 py-2 border-b border-border bg-secondary/30">
            <div className="label-mono">{group}</div>
          </div>
          <table className="w-full data-table text-sm">
            <thead>
              <tr>
                <th className="w-44">Role</th>
                {perms.map((p) => (
                  <th key={p} className="text-center text-[11px] font-medium" title={p}>
                    {p.replace(/_/g, " ")}
                  </th>
                ))}
                <th className="w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => {
                const locked = role === "super_admin";
                const dirtyRow = isRowDirty(role);
                return (
                  <tr key={role} className={dirtyRow ? "bg-[hsl(38_92%_50%)]/5" : ""} data-testid={`perm-row-${role}`}>
                    <td className="font-medium capitalize flex items-center gap-1.5">
                      {locked && <Lock className="w-3 h-3 text-muted-foreground" />}
                      {role.replace(/_/g, " ")}
                    </td>
                    {perms.map((p) => {
                      const v = effective(role, p);
                      const orig = matrix[role][p];
                      const def = defaults[role]?.[p] || false;
                      const changedFromOrig = (dirty[role]?.[p] !== undefined) && (dirty[role][p] !== orig);
                      const overrideOfDefault = orig !== def;
                      return (
                        <td key={p} className="text-center">
                          <button
                            onClick={() => toggle(role, p)}
                            disabled={locked}
                            data-testid={`perm-cell-${role}-${p}`}
                            title={`${p}${overrideOfDefault ? " (override of default)" : ""}`}
                            className={`w-6 h-6 inline-flex items-center justify-center rounded-sm border ${
                              locked ? "border-border bg-secondary/40 opacity-60 cursor-not-allowed" :
                              v ? "border-primary bg-primary text-white" : "border-border bg-card hover:bg-secondary"
                            } ${changedFromOrig ? "ring-2 ring-[hsl(38_92%_50%)]" : ""}`}
                          >
                            {v && <span className="text-[11px] leading-none">✓</span>}
                          </button>
                        </td>
                      );
                    })}
                    <td className="text-right whitespace-nowrap">
                      {!locked && (
                        <>
                          <button
                            onClick={() => saveRole(role)}
                            disabled={!dirtyRow || savingRole === role}
                            data-testid={`perm-save-${role}`}
                            className="h-7 px-2 text-[11px] font-medium bg-primary text-primary-foreground rounded-sm disabled:opacity-40 inline-flex items-center gap-1"
                          >
                            <Save className="w-3 h-3" /> {savingRole === role ? "Saving…" : "Save"}
                          </button>
                          <button
                            onClick={() => resetRole(role)}
                            data-testid={`perm-reset-${role}`}
                            className="h-7 px-2 text-[11px] font-medium border border-border rounded-sm ml-1 hover:bg-secondary inline-flex items-center gap-1"
                            title="Drop override and revert to platform defaults"
                          >
                            <RotateCcw className="w-3 h-3" /> Reset
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
